import React from 'react';
import { Card, Typography, Empty } from 'antd';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import { HistoryEntry, DisplayHints } from './types';
import moment from 'moment';

const { Text } = Typography;

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  Filler
);

interface MetricsTimelineProps {
  metricKey: string;
  title: string;
  history: HistoryEntry[];
  displayHints?: DisplayHints;
}

/**
 * Extract numeric value from history entry data
 */
const extractValue = (data: any): number | null => {
  if (data === null || data === undefined) return null;

  // Handle total_hits
  if (typeof data === 'object' && 'total_hits' in data) {
    const val = parseInt(data.total_hits, 10);
    return isNaN(val) ? null : val;
  }

  // Handle direct number
  if (typeof data === 'number') return data;

  // Handle string number
  if (typeof data === 'string') {
    const val = parseFloat(data);
    return isNaN(val) ? null : val;
  }

  // Handle count field
  if (typeof data === 'object' && 'count' in data) {
    const val = parseInt(data.count, 10);
    return isNaN(val) ? null : val;
  }

  return null;
};

export const MetricsTimeline: React.FC<MetricsTimelineProps> = ({
  metricKey,
  title,
  history,
  displayHints
}) => {
  // Process history data for chart
  const chartData = history
    .map(entry => ({
      x: new Date(entry.t),
      y: extractValue(entry.data),
      count: entry.count
    }))
    .filter(point => point.y !== null)
    .sort((a, b) => a.x.getTime() - b.x.getTime());

  if (chartData.length === 0) {
    return (
      <Card
        size="small"
        style={{
          borderRadius: '8px',
          border: '1px solid #f0f0f0'
        }}
        bodyStyle={{ padding: '16px' }}
      >
        <Text type="secondary" style={{ fontSize: '12px', marginBottom: '8px', display: 'block' }}>
          {title}
        </Text>
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No time-series data available"
          style={{ margin: '16px 0' }}
        />
      </Card>
    );
  }

  const color = displayHints?.color || '#1890ff';

  const data = {
    datasets: [
      {
        label: title,
        data: chartData,
        borderColor: color,
        backgroundColor: `${color}20`,
        fill: true,
        tension: 0.3,
        pointRadius: 3,
        pointHoverRadius: 5
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          title: (context: any) => {
            const date = new Date(context[0].parsed.x);
            return moment(date).format('MMM D, HH:mm');
          },
          label: (context: any) => {
            const unit = displayHints?.unit || '';
            return `${context.parsed.y} ${unit}`.trim();
          }
        }
      }
    },
    scales: {
      x: {
        type: 'time' as const,
        time: {
          unit: 'hour' as const,
          displayFormats: {
            hour: 'HH:mm'
          }
        },
        grid: {
          display: false
        },
        ticks: {
          font: {
            size: 10
          }
        }
      },
      y: {
        beginAtZero: true,
        grid: {
          color: '#f0f0f0'
        },
        ticks: {
          font: {
            size: 10
          }
        }
      }
    }
  };

  return (
    <Card
      size="small"
      style={{
        borderRadius: '8px',
        border: '1px solid #f0f0f0',
        boxShadow: '0 1px 4px rgba(0,0,0,0.04)'
      }}
      bodyStyle={{ padding: '16px' }}
    >
      <Text type="secondary" style={{ fontSize: '12px', marginBottom: '12px', display: 'block' }}>
        {title}
      </Text>
      <div style={{ height: '200px' }}>
        <Line data={data} options={options} />
      </div>
    </Card>
  );
};

export default MetricsTimeline;
